import {useEffect, useMemo, useRef, useState} from 'react';
import {ehrService} from '../../services/ehrService';
import {
  computeAppointmentStatus,
  appointmentSortKey,
} from '../../utils/appointmentStatus';

/**
 * Hook that loads today's cached EHR appointments for the user's company
 * and returns, for each patient, the "best" (highest-priority) status —
 * i.e. if a patient has an active visit AND a later visit today, the
 * active one wins.
 *
 * The status is recomputed every `refreshIntervalMs` (default 60s) so the
 * UI transitions "soon → now → justEnded → past" without a page reload.
 *
 * We fetch the raw appointments once per mount and then re-classify them
 * locally on each tick — no extra Firestore reads while the tab is open.
 *
 * When a `provider` (the logged-in user) is supplied, the result is scoped
 * to that provider's own appointments — matched against each appointment's
 * cached `providerName`/`providerId`. If we can't confidently identify any
 * of the user's appointments today (provider unknown, admin user, name
 * mismatch, …) we fall back to showing everyone's so the section is never
 * empty just because matching failed.
 *
 * Usage:
 *   const { statusByPatientId, timeZone, ready } = useAppointmentStatuses({
 *     companyId, hasActiveEhr, ehrConfig,
 *     provider: { displayName: userData?.displayName, email: userData?.email },
 *   });
 *   // statusByPatientId.get('12345') -> { kind: 'now', start, end, ... }
 */
export default function useAppointmentStatuses({
  companyId,
  hasActiveEhr,
  ehrConfig,
  provider = null,
  refreshIntervalMs = 60_000,
  // Bump to force a fresh Firestore re-fetch of today's appointments
  // (the steady-state 60s tick only re-classifies the cached rows, it
  // does NOT re-read). Used after a `/sync/me` that reported new
  // same-day appointments so they appear without a remount.
  reloadToken = 0,
}: Record<string, any>) {
  const [rawAppointments, setRawAppointments] = useState<any[]>([]);
  const [ready, setReady] = useState(false);
  const [tick, setTick] = useState(0); // bumped on every refresh to force recompute
  const mountedRef = useRef(true);

  const timeZone = useMemo(
    () => ehrService.resolveTimeZone(ehrConfig),
    [ehrConfig],
  );

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Fetch today's appointments once whenever inputs change.
  useEffect(() => {
    if (!hasActiveEhr || !companyId) {
      setRawAppointments([]);
      setReady(true);
      return undefined;
    }

    let cancelled = false;
    setReady(false);

    (async () => {
      try {
        const today = facilityDateString(new Date(), timeZone);
        const list = await ehrService.fetchCachedAppointments(companyId, [
          today,
        ]);
        if (cancelled) return;
        setRawAppointments(list || []);
      } catch (err) {
        console.error("Failed to fetch today's appointments:", err);
        if (!cancelled) setRawAppointments([]);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, hasActiveEhr, timeZone, reloadToken]);

  // Re-classify every `refreshIntervalMs` without re-fetching.
  useEffect(() => {
    if (!hasActiveEhr) return undefined;
    const id = setInterval(() => {
      if (mountedRef.current) setTick(t => t + 1);
    }, refreshIntervalMs);
    return () => clearInterval(id);
  }, [hasActiveEhr, refreshIntervalMs]);

  // Tokenized identity for the logged-in provider, derived from their
  // display name (falling back to the email local part). Recomputed only
  // when the user changes. Empty when we can't identify the user — which
  // disables scoping (we then show all appointments).
  const providerTokens = useMemo(
    () => deriveProviderTokens(provider),
    // Depend on the stable primitive fields, not the fresh `provider` object
    // literal the call sites pass each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [provider?.displayName, provider?.email],
  );
  // Kept as a stable primitive so the memo below doesn't recompute every
  // render just because `provider` is a fresh object literal at the call site.
  const providerId =
    provider?.providerId != null ? String(provider.providerId) : null;

  const statusByPatientId = useMemo(() => {
    const map = new Map();
    if (!rawAppointments.length) return map;
    const now = new Date();

    // Scope to the current provider's own appointments when we can identify
    // them; if none match, fall back to all so we never hide the section
    // because of a name mismatch.
    let scoped = rawAppointments;
    if (providerTokens.length >= 2 || providerId) {
      const mine = rawAppointments.filter(appt =>
        appointmentMatchesProvider(appt, providerId, providerTokens),
      );
      if (mine.length) scoped = mine;
    }

    for (const appt of scoped) {
      const pid = appt.patientId;
      if (!pid) continue;

      const status = computeAppointmentStatus(
        {
          appointmentDate: appt.appointmentDate,
          appointmentTime: appt.appointmentTime,
          appointmentEnd: appt.appointmentEnd,
          status: appt.status,
        },
        now,
        timeZone,
      );
      if (status.kind === 'unknown' || status.kind === 'past') continue;

      const existing = map.get(pid);
      if (
        !existing ||
        appointmentSortKey(status) < appointmentSortKey(existing)
      ) {
        map.set(pid, status);
      }
    }

    return map;
    // Intentionally depend on `tick` so we recompute on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawAppointments, timeZone, tick, providerTokens, providerId]);

  return {statusByPatientId, timeZone, ready};
}

/**
 * Format "now" as `YYYY-MM-DD` in the facility timezone so it can be used
 * as the key for `fetchCachedAppointments`'s range query. Does NOT use
 * device-local date — a user in PT looking at an ET practice at 22:00 PT
 * should see ET's "tomorrow" already.
 */
function facilityDateString(instant: any, timeZone: any) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

// Titles / credentials / suffixes that carry no identifying signal and
// would otherwise create false matches between different providers.
const NAME_NOISE = new Set([
  'dr',
  'doctor',
  'md',
  'do',
  'np',
  'pa',
  'rn',
  'lpn',
  'phd',
  'mbbs',
  'mr',
  'mrs',
  'ms',
  'miss',
  'prof',
  'professor',
  'jr',
  'sr',
  'ii',
  'iii',
  'iv',
]);

// Split a name into lowercase, alphabetic tokens, dropping titles/suffixes
// and single characters (initials) so "Dr. Jane M. Smith" and
// "Smith, Jane MD" both reduce to {jane, smith}.
function normalizeNameTokens(name: any) {
  if (!name || typeof name !== 'string') return [];
  return name
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .split(' ')
    .filter(t => t.length >= 2 && !NAME_NOISE.has(t));
}

// Best-effort identity tokens for the logged-in user. Prefer the display
// name; if that yields fewer than two tokens, fall back to the email local
// part (e.g. "jane.smith@clinic.com" -> {jane, smith}).
function deriveProviderTokens(provider: any) {
  if (!provider) return [];
  const fromName = normalizeNameTokens(provider.displayName);
  if (fromName.length >= 2) return fromName;
  if (provider.email) {
    const local = String(provider.email).split('@')[0] || '';
    const fromEmail = normalizeNameTokens(local.replace(/[._-]+/g, ' '));
    if (fromEmail.length >= 2) return fromEmail;
  }
  return fromName;
}

// Does this appointment belong to the logged-in provider? Matches on the
// EHR `providerId` when both sides have one, otherwise on a name overlap of
// at least two tokens (first + last) to avoid first-name-only collisions.
function appointmentMatchesProvider(
  appt: any,
  providerId: any,
  providerTokens: any,
) {
  if (!appt) return false;
  if (
    providerId &&
    appt.providerId != null &&
    providerId === String(appt.providerId)
  ) {
    return true;
  }
  if (providerTokens.length < 2) return false;
  const apptTokens = new Set(normalizeNameTokens(appt.providerName));
  if (!apptTokens.size) return false;
  let overlap = 0;
  for (const token of providerTokens) {
    if (apptTokens.has(token)) overlap += 1;
    if (overlap >= 2) return true;
  }
  return false;
}
